import { toast } from 'react-hot-toast';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SocketContext } from '../SocketContext';
import { RootState, useAppDispatch, useAppSelector } from '../store';
import { ChatList, ChatListGroup } from '../Types';
import { ChatDeleteMessage, SocketMessageTypes, TitleListUpdateMessage } from '../Types/SocketMessage';
import { setChatlistClear } from '../Store/features/environment/environmentSlice';
import { chatService } from '../Services/chatService';
import { setNavigationSubTitle, setNavigationTitle } from '../Store/features/navigationHeader/navigationHeaderSlice';
import usePromptState from './state/usePromptState';
import useModal from '../components/Modal/useModal';

const useChatList = () => {
    const navigate = useNavigate();
    const currentURL = useAppSelector((state: RootState) => state.environment.currentURL);
    const i18n = useAppSelector((state: RootState) => state.i18n.i18n);
    const dispatch = useAppDispatch();
    const isCtrlOrCmdPressed = useAppSelector((state: RootState) => state.environment.isCtrlOrCmdPressed);
    const selectedTheme = useAppSelector((state: RootState) => (state as any).themes?.selectedTheme || '');
    const currentChatId = useAppSelector((state: RootState) => state.environment.currentChatId);
    const { prompts, basePrompt, setSelectedPrompt, setCombinedPrompts } = usePromptState();

    const { addMessageHandler } = useContext(SocketContext);

    const Currentsettings = useAppSelector((state: RootState) => state.settings);
    const chatList = useAppSelector((state: RootState) => state.chatlist.chatlist as ChatList[]);
    const chatlistOpen = useAppSelector((state: RootState) => state.chatlist.chatlistOpen as boolean);
    const chatlistOpenHover = useAppSelector((state: RootState) => state.chatlist.chatlistOpenHover as boolean);
    const [ListItemEdit, setListItemEdit] = useState<string>('');
    const [filterValue, setFilter] = useState<string>('');
    const [chatsFetched, setChatsFetched] = useState<boolean>(false);
    const [allChatsLoaded, setAllChatsLoaded] = useState<ChatList[]>([]);

    const modal = useModal();

    const themes = useAppSelector((state: RootState) => (state as any).themes?.allThemes || {});
    const themeLogo =
        themes[selectedTheme]?.themeColor === 'light'
            ? Currentsettings.theme.lightThemeImage
                ? Currentsettings.theme.lightThemeImage
                : themes[selectedTheme]?.logo ?? ''
            : Currentsettings.theme.darkThemeImage
            ? Currentsettings.theme.darkThemeImage
            : themes[selectedTheme]?.logo ?? '';
    const clearChatlist = useAppSelector((state: RootState) => state.environment.chatlistClear);

    const fetchAllChats = useCallback(async () => {
        try {
            setChatsFetched(false);
            let allChats: ChatList[] = [];
            let currentPage = 1;
            let hasMorePages = true;
            const itemsPerPage = 50;

            while (hasMorePages) {
                const response = await chatService.getChatListByPage(currentPage, itemsPerPage);
                const pageData = response.data.data;

                allChats = [...allChats, ...pageData];

                hasMorePages = pageData.length === itemsPerPage;
                currentPage++;
            }

            setAllChatsLoaded(allChats);
            setChatsFetched(true);
        } catch (error) {
            console.error('Error fetching chats:', error);
            setChatsFetched(true);
        }
    }, []);

    const handleChatUpdated = useCallback((data: any) => {
        const chatIdentifier = data.chatIdentifier || data.identifier;
        if (!chatIdentifier) return;
        setAllChatsLoaded((prev) => {
            const next = prev.map((chat) => {
                if (chat.identifier === chatIdentifier) {
                    return {
                        ...chat,
                        updatedAt: data.updatedAt,
                        title: data.title ?? chat.title,
                    };
                }
                return chat;
            });

            return next;
        });
    }, []);

    useEffect(() => {
        addMessageHandler(SocketMessageTypes.chatUpdated, handleChatUpdated);
    }, [addMessageHandler, handleChatUpdated]);

    const resetChats = useCallback(() => {
        setAllChatsLoaded([]);
        fetchAllChats();
    }, [fetchAllChats]);

    const filteredChats = useMemo(() => {
        if (!filterValue.trim()) {
            return allChatsLoaded;
        }

        return allChatsLoaded.filter(
            (chat) =>
                chat.title.toLowerCase().includes(filterValue.toLowerCase().trim()) ||
                chat.assistantTitle?.toLowerCase().includes(filterValue.toLowerCase().trim()),
        );
    }, [allChatsLoaded, filterValue]);

    const handleChatDeleted = useCallback(
        (data: ChatDeleteMessage) => {
            const updatedAllChats = allChatsLoaded.filter(
                (chatListItem: ChatList) => chatListItem.identifier !== data.chatIdentifier,
            );

            setAllChatsLoaded(updatedAllChats);

            if (data.chatIdentifier === currentChatId) {
                navigate('/chats');
            }
        },
        [currentChatId, navigate, allChatsLoaded],
    );

    const navigateToHome = useCallback(() => {
        setSelectedPrompt(basePrompt!);
        navigate('/start');
    }, [basePrompt, navigate, setSelectedPrompt]);

    const deleteChat = useCallback(
        async (chatIdentifier: string) => {
            if (chatIdentifier === currentChatId) {
                dispatch(setNavigationTitle(''));
                dispatch(setNavigationSubTitle(''));
            }
            chatService.deleteChat(chatIdentifier).then(() => {
                handleChatDeleted({ chatIdentifier: chatIdentifier });

                toast.success(i18n.translate({ id: 'toast.chatDeleted', sourceName: 'ChatList' }));
            });
        },
        [handleChatDeleted, i18n, dispatch, currentChatId],
    );

    const deleteChatHandler = useCallback(
        (chatIdentifier: string) => {
            if (isCtrlOrCmdPressed) {
                deleteChat(chatIdentifier);
            } else {
                modal.deleteConfirm({
                    title: i18n.translate({ id: 'chatDelete.conversation', sourceName: 'Chat' }),
                    content: i18n.translate({ id: 'chat.conversation', sourceName: 'Chat' }),
                    onConfirm: () => {
                        deleteChat(chatIdentifier);
                    },
                    size: 'medium',
                });
            }
        },
        [deleteChat, modal, i18n, isCtrlOrCmdPressed],
    );

    const handleTitleListUpdate = useCallback(
        (data: TitleListUpdateMessage) => {
            const updatedAllChats = allChatsLoaded.map((chatlistItem: ChatList) =>
                chatlistItem.identifier === data.chatIdentifier ? { ...chatlistItem, title: data.title } : chatlistItem,
            );

            setAllChatsLoaded(updatedAllChats);
        },
        [allChatsLoaded],
    );

    const updateTitle = useCallback(
        async (chatIdentifier: string, chatTitle: string) => {
            chatService.updateChat(chatIdentifier, chatTitle).then(() => {
                setListItemEdit('');
                navigate(`/chats/${chatIdentifier}`);
                handleTitleListUpdate({ chatIdentifier: chatIdentifier, title: chatTitle });
            });
        },
        [navigate, setListItemEdit, handleTitleListUpdate],
    );

    const headlines = useMemo(
        () => ({
            today: i18n.translate({ id: 'sort.today', sourceName: 'ChatList' }),
            yesterday: i18n.translate({ id: 'sort.yesterday', sourceName: 'ChatList' }),
            week: i18n.translate({ id: 'sort.week', sourceName: 'ChatList' }),
            month: i18n.translate({ id: 'sort.month', sourceName: 'ChatList' }),
            older: i18n.translate({ id: 'sort.older', sourceName: 'ChatList' }),
        }),
        [i18n],
    );

    const rangeToHeadline = useMemo(
        () => [
            { maxDays: 0, headline: headlines.today },
            { maxDays: 1, headline: headlines.yesterday },
            { maxDays: 7, headline: headlines.week },
            { maxDays: 30, headline: headlines.month },
        ],
        [headlines],
    );

    const groupChatsByHeadline = useCallback(
        (chatlist: ChatList[]) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const sortedChatList = [...chatlist].sort((a, b) => {
                const dateA = new Date(a.updatedAt).getTime();
                const dateB = new Date(b.updatedAt).getTime();
                return dateB - dateA;
            });

            const chatListWithHeadlines = sortedChatList.map((chatlistItem) => {
                const chatDate = new Date(chatlistItem.updatedAt);
                chatDate.setHours(0, 0, 0, 0);

                const diffInDays = Math.floor((today.getTime() - chatDate.getTime()) / (1000 * 60 * 60 * 24));

                const headline =
                    rangeToHeadline.find((range) => diffInDays <= range.maxDays)?.headline || headlines.older;

                return { ...chatlistItem, headline };
            });

            const grouped = chatListWithHeadlines.reduce(
                (
                    groupedChatlistItems: {
                        [key: string]: ChatList[];
                    },
                    chatlistItem: ChatList & ChatListGroup,
                ) => {
                    (groupedChatlistItems[chatlistItem.headline] =
                        groupedChatlistItems[chatlistItem.headline] || []).push(chatlistItem);
                    return groupedChatlistItems;
                },
                {},
            );

            Object.keys(grouped).forEach((key) => {
                grouped[key].sort((a, b) => {
                    const dateA = new Date(a.updatedAt).getTime();
                    const dateB = new Date(b.updatedAt).getTime();
                    return dateB - dateA;
                });
            });

            return grouped;
        },
        [rangeToHeadline, headlines],
    );

    const groupedChats = useMemo(() => groupChatsByHeadline(filteredChats), [groupChatsByHeadline, filteredChats]);

    useEffect(() => {
        chatService.getBaseAndLastUpdatedPrompt().then((response: any) => {
            setCombinedPrompts(response.data);
        });
    }, [setCombinedPrompts, prompts, chatList]);

    useEffect(() => {
        fetchAllChats();
    }, [fetchAllChats]);

    useEffect(() => {
        if (clearChatlist) {
            setAllChatsLoaded([]);
            fetchAllChats();
            dispatch(setChatlistClear(false));
        }
    }, [fetchAllChats, clearChatlist, dispatch]);

    useEffect(() => {
        addMessageHandler(SocketMessageTypes.chatDeleted, handleChatDeleted);
        addMessageHandler(SocketMessageTypes.titleListUpdate, handleTitleListUpdate);
        addMessageHandler(SocketMessageTypes.chatCreated, resetChats);
    }, [addMessageHandler, resetChats, handleChatDeleted, handleTitleListUpdate]);

    const ChatListActions = useMemo(
        () => ({
            deleteChat,
            deleteChatHandler,
            updateTitle,
            fetchAllChats,
            groupChatsByHeadline,
            navigateToHome,
            resetChats,
        }),
        [deleteChat, deleteChatHandler, updateTitle, fetchAllChats, groupChatsByHeadline, navigateToHome, resetChats],
    );

    return {
        ChatListActions,
        i18n,
        chatlistOpen,
        chatlistOpenHover,
        ListItemEdit,
        setListItemEdit,
        filterValue,
        setFilter,
        themeLogo,
        chatsFetched,
        groupedChats,
        currentURL,
        dispatch,
        allChatsLoaded: filteredChats,
    };
};

export default useChatList;
